import { lazy, Suspense, useEffect } from 'react';
import './App.scss';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { loginRoutes, routes } from './utils/routes';
import OomeroUtils from './utils/OomeroUtils';
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { responseInterceptor } from './utils/api';
import useActivityTracker from './hooks/useActivityTracker';
import RouteFallback from './components/Loader/RouteFallback';
import { saveDetectedLocation } from './utils/location';

// Any address no route knows (2 Oct 2026); loaded only when needed.
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

const EUROPE_COUNTRY_CODES = new Set([
  'ad', 'al', 'at', 'ax', 'ba', 'be', 'bg', 'by', 'ch', 'cy', 'cz', 'de', 'dk', 'ee',
  'es', 'fi', 'fo', 'fr', 'gb', 'gg', 'gi', 'gr', 'hr', 'hu', 'ie', 'im', 'is', 'it',
  'je', 'li', 'lt', 'lu', 'lv', 'mc', 'md', 'me', 'mk', 'mt', 'nl', 'no', 'pl', 'pt',
  'ro', 'rs', 'ru', 'se', 'si', 'sj', 'sk', 'sm', 'tr', 'ua', 'va', 'xk'
]);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const shouldRequestLocation = location.pathname === '/login' || location.pathname === '/dashboard' || location.pathname === '/';

  // Initialize activity tracker for auto-logout after 15 minutes of inactivity
  useActivityTracker(15);

  useEffect(() => {
    responseInterceptor(navigate);
  }, []); // Call once on mount

  useEffect(() => {
    if (!shouldRequestLocation) {
      return;
    }

    if (!navigator?.geolocation) {
      return;
    }

    let permissionWatcher;

    const persistLocation = async (position) => {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`, {
          headers: {
            Accept: 'application/json',
          },
        });

        const geocodeData = response.ok ? await response.json() : null;
        const address = geocodeData?.address || {};
        const countryCode = (address.country_code || '').toLowerCase();
        const city = address.city || address.town || address.village || address.county || address.state_district || '';
        const stateName = address.state || address.region || '';
        const country = address.country || '';
        const isEurope = EUROPE_COUNTRY_CODES.has(countryCode);

        saveDetectedLocation({
          latitude,
          longitude,
          countryCode: countryCode ? countryCode.toUpperCase() : '',
          city,
          state: stateName,
          country,
          isEurope,
          label: [city, stateName, country].filter(Boolean).join(', ') || `Lat ${latitude.toFixed(4)}, Lng ${longitude.toFixed(4)}`,
        });
      } catch (error) {
        saveDetectedLocation({
          latitude,
          longitude,
          label: `Lat ${latitude.toFixed(4)}, Lng ${longitude.toFixed(4)}`,
        });
      }
    };

    const requestLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          persistLocation(position);
        },
        () => {
          localStorage.setItem('detectedLocationPermission', 'denied');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    };

    const initLocationPermission = async () => {
      if (!navigator?.permissions?.query) {
        requestLocation();
        return;
      }

      try {
        permissionWatcher = await navigator.permissions.query({ name: 'geolocation' });
        localStorage.setItem('detectedLocationPermission', permissionWatcher.state);

        if (permissionWatcher.state === 'prompt' || permissionWatcher.state === 'granted') {
          requestLocation();
        }

        permissionWatcher.onchange = () => {
          localStorage.setItem('detectedLocationPermission', permissionWatcher.state);
          if (permissionWatcher.state === 'granted') {
            requestLocation();
          }
        };
      } catch (error) {
        requestLocation();
      }
    };

    initLocationPermission();

    return () => {
      if (permissionWatcher) {
        permissionWatcher.onchange = null;
      }
    };
  }, [shouldRequestLocation]);

  return (
    <>
    <OomeroUtils.ScrollToTop/>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {
            loginRoutes.map((route, key) => {
              return <Route key={key} {...route} />
            })
          }
          {
            routes.map((route, key) => {
              return <Route key={key} {...route} />
            })
          }
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={4}
        theme="colored"
        transition={Slide}
        className="modern-toast-container"
        toastClassName="modern-toast"
        bodyClassName="modern-toast-body"
      />
    </>
  );
}

export default App;
